import { ButtonColor } from 'bento';
import { Theme, css } from '@emotion/react';

const getButtonColors = ({ colors }: Theme, color: ButtonColor) => {
  switch (color) {
    case 'primary':
      return css`
        --button-background: ${colors.bgPrimaryBase};
        --button-background-hover: ${colors.bgPrimaryDark};
        --button-outline-background-hover: ${colors.bgPrimaryLighter};
        --button-outline-color: ${colors.borderPrimaryBase};
        --button-foreground: ${colors.fgContrast};
        --button-border: ${colors.borderPrimaryBase};

        --contained-disabled-background: ${colors.bgLight};
        --contained-disabled-foreground: ${colors.fgContrast};

        --outlined-foreground: ${colors.fgPrimaryBase};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDefault};

        --text-foreground: ${colors.fgPrimaryBase};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
    case 'subtle':
      return css`
        --button-background: ${colors.bgDark};
        --button-background-hover: ${colors.bgDarker};
        --button-outline-background-hover: ${colors.bgLighter};
        --button-outline-color: ${colors.borderPrimaryBase};
        --button-foreground: ${colors.fgContrast};
        --button-border: ${colors.borderDefault};

        --contained-disabled-background: ${colors.bgLight};
        --contained-disabled-foreground: ${colors.fgContrast};

        --outlined-foreground: ${colors.fgDefault};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDefault};

        --text-foreground: ${colors.fgDefault};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
    case 'contrast':
      return css`
        --button-background: ${colors.bgDefault};
        --button-background-hover: ${colors.bgLight};
        --button-outline-background-hover: ${colors.bgDarker};
        --button-outline-color: ${colors.borderContrast};
        --button-foreground: ${colors.fgDefault};
        --button-border: ${colors.borderContrast};

        --contained-disabled-background: ${colors.bgDark};
        --contained-disabled-foreground: ${colors.fgDefault};

        --outlined-foreground: ${colors.fgContrast};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDark};

        --text-foreground: ${colors.fgContrast};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
    case 'success':
      return css`
        --button-background: ${colors.bgSuccessBase};
        --button-background-hover: ${colors.bgSuccessDark};
        --button-outline-background-hover: ${colors.bgSuccessLighter};
        --button-outline-color: ${colors.borderPrimaryBase};
        --button-foreground: ${colors.fgContrast};
        --button-border: ${colors.borderSuccessBase};

        --contained-disabled-background: ${colors.bgLight};
        --contained-disabled-foreground: ${colors.fgContrast};

        --outlined-foreground: ${colors.fgSuccessBase};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDefault};

        --text-foreground: ${colors.fgSuccessBase};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
    case 'danger':
      return css`
        --button-background: ${colors.bgDangerBase};
        --button-background-hover: ${colors.bgDangerDark};
        --button-outline-background-hover: ${colors.bgDangerLighter};
        --button-outline-color: ${colors.borderPrimaryBase};
        --button-foreground: ${colors.fgContrast};
        --button-border: ${colors.borderDangerBase};

        --contained-disabled-background: ${colors.bgLight};
        --contained-disabled-foreground: ${colors.fgContrast};

        --outlined-foreground: ${colors.fgDangerBase};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDefault};

        --text-foreground: ${colors.fgDangerBase};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
    case 'internal':
      return css`
        --button-background: ${colors.bgInternalBase};
        --button-background-hover: ${colors.bgInternalDark};
        --button-outline-background-hover: ${colors.bgInternalLighter};
        --button-outline-color: ${colors.borderPrimaryBase};
        --button-foreground: ${colors.fgContrast};
        --button-border: ${colors.borderInternalBase};

        --contained-disabled-background: ${colors.bgLight};
        --contained-disabled-foreground: ${colors.fgContrast};

        --outlined-foreground: ${colors.fgInternalBase};
        --outlined-disabled-foreground: ${colors.fgDisabled};
        --outlined-disabled-border: ${colors.borderDefault};

        --text-foreground: ${colors.fgInternalBase};
        --text-disabled-foreground: ${colors.fgDisabled};
      `;
  }
};

export const commonStyles = (color: ButtonColor) => (theme: Theme) => css`
  ${getButtonColors(theme, color)};

  :hover,
  :active,
  :focus {
    box-shadow: none;
  }

  ${theme.interactiveStates.focus(css`
    box-shadow: none;
    outline: 2px solid var(--button-outline-color);
    outline-offset: 2px;
  `)}

  @media (hover: none) {
    box-shadow: none;
  }
`;

const containedStyle = ({ interactiveStates }: Theme) => css`
  background-color: var(--button-background);
  color: var(--button-foreground);

  ${interactiveStates.hover(css`
    background-color: var(--button-background-hover);
  `)}

  ${interactiveStates.active(css`
    background-color: var(--button-background-hover);
  `)}

  ${interactiveStates.disabled(css`
    background-color: var(--contained-disabled-background);
    color: var(--contained-disabled-foreground);
  `)}
`;

const textStyle = ({ interactiveStates }: Theme) => css`
  background-color: transparent;
  color: var(--text-foreground);

  ${interactiveStates.hover(css`
    background-color: var(--button-outline-background-hover);
  `)}

  ${interactiveStates.active(css`
    background-color: var(--button-outline-background-hover);
  `)}

  ${interactiveStates.disabled(css`
    color: var(--text-disabled-foreground);
  `)}
`;

const outlinedStyle = ({ interactiveStates }: Theme) => css`
  color: var(--outlined-foreground);
  border: solid 1px var(--button-border);

  ${interactiveStates.hover(css`
    background-color: var(--button-outline-background-hover);
  `)}

  ${interactiveStates.active(css`
    background-color: var(--button-outline-background-hover);
  `)}

  ${interactiveStates.disabled(css`
    color: var(--outlined-disabled-foreground);
    border-color: var(--outlined-disabled-border);
  `)}
`;

export const buttonVariantStyles = {
  contained: containedStyle,
  outlined: outlinedStyle,
  text: textStyle,
};

const smallStyle = ({ spacing, typography }: Theme) => css`
  ${typography.variant('bodyStrong')};
  line-height: ${typography.pxToRem(16)};
  padding: ${spacing(0.5)}px ${spacing(1)}px;
  min-height: ${spacing(4)}px;
`;

const mediumStyle = ({ spacing, typography }: Theme) => css`
  ${typography.variant('bodyStrong')};
  line-height: ${typography.pxToRem(18)};
  padding: ${spacing(0.5)}px ${spacing(1.5)}px;
  min-height: ${spacing(4.5)}px;
`;

const largeStyle = ({ spacing, typography }: Theme) => css`
  ${typography.variant('bodyStrong')};
  line-height: ${typography.pxToRem(18)};
  padding: ${spacing(0.5)}px ${spacing(2)}px;
  min-height: ${spacing(5.5)}px;
`;

export const buttonSizeStyles = {
  small: smallStyle,
  medium: mediumStyle,
  large: largeStyle,
};

export const iconSizes = ({ typography }: Theme) => css`
  .MuiButton-iconSizeSmall > svg,
  .MuiButton-iconSizeMedium > svg,
  .MuiButton-iconSizeLarge > svg {
    font-size: ${typography.pxToRem(20)};
  }
`;
